/**
 * Water & Reporting — ONE editor, every surface.
 *
 * The jurisdiction/provider picker, portal account number, portal link, and
 * on-site contact live here and ONLY here. Mounted on:
 *   1. the site/location page (Water & Reporting rail card),
 *   2. the job page's Water & Reporting card,
 *   3. the customer Locations-tab Add/Edit Location dialog.
 *
 * Canonical column doctrine:
 *   - The portal account number is `sites.water_account_number`. Every
 *     surface reads and writes THAT column.
 *   - `sites.account_number` is the legacy ServiceTitan location code. It
 *     survives as a dispatch-gate fallback and shows only as a muted
 *     "Legacy ST code" line — NEVER inside a portal-account field.
 *
 * Saves from any surface invalidate one shared key set
 * (`invalidateWaterReporting`) so the other two surfaces — plus the job
 * reporting card, pre-job gate, and City Filing Queue — reflect the change
 * without a reload, and the Edit Location dialog can never render a stale
 * cached row again (it reads its own fresh `["site-water", id]` query, not
 * the locations-list cache).
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Droplet, ExternalLink, Loader2, Save, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { openExternalLink } from "@/lib/standalone";

// ── Shapes ──────────────────────────────────────────────────────────────────

export interface WaterProviderOption {
    id: string;
    name: string;
    display_name?: string | null;
    report_name?: string | null;
    parent_water_provider_id?: string | null;
    submission_platform: string | null;
    portal_url: string | null;
    is_portal?: boolean | null;
}

export interface SiteContactOption {
    id: string;
    first_name: string;
    last_name: string | null;
    role_title: string | null;
}

/** The editable facts. Strings, "" = unset — plain controlled-input state. */
export interface WaterReportingValue {
    waterProviderId: string;
    /** Canonical portal account (`sites.water_account_number`). */
    waterAccountNumber: string;
    /** On-site phone (`sites.phone`). */
    phone: string;
    /** On-site contact (`sites.default_contact_id`). */
    defaultContactId: string;
}

export function useWaterProviders() {
    return useQuery({
        queryKey: ["water-providers", "active"],
        queryFn: () => api.get<{ providers: WaterProviderOption[] }>("/api/water-providers?active=true"),
        staleTime: 5 * 60_000,
    });
}

export function useSiteContacts(customerId: string | null | undefined) {
    return useQuery({
        queryKey: ["customer", customerId, "contacts"],
        queryFn: () => api.get<{ contacts: SiteContactOption[] }>(`/api/customers/${customerId}/contacts`),
        enabled: !!customerId,
    });
}

// ── Shared invalidation ─────────────────────────────────────────────────────

/**
 * One key set for every Water & Reporting read in the app. A save from ANY
 * surface calls this, so the other surfaces reflect it immediately:
 *   - the editor's own fresh read + the site page summary
 *   - the customer Locations tab (list rows feed the table, never the dialog)
 *   - every open job header (reporting card, §3f gating, stepper increments
 *     all derive from the job payload's provider joins) + the field app
 *   - the pre-job gate list and the City Filing Queue
 */
export function invalidateWaterReporting(qc: QueryClient, opts: { siteId: string; customerId?: string | null }): void {
    void qc.invalidateQueries({ queryKey: ["site-water", opts.siteId] });
    void qc.invalidateQueries({ queryKey: ["site-summary", opts.siteId] });
    void qc.invalidateQueries({ queryKey: ["site-detail", opts.siteId] });
    void qc.invalidateQueries({ queryKey: ["site", opts.siteId] });
    if (opts.customerId) {
        void qc.invalidateQueries({ queryKey: ["customer", opts.customerId, "sites"] });
    } else {
        void qc.invalidateQueries({ queryKey: ["customer"] });
    }
    void qc.invalidateQueries({ queryKey: ["job"] });
    void qc.invalidateQueries({ queryKey: ["field", "job-detail"] });
    void qc.invalidateQueries({ queryKey: ["jobs", "pre-job-review"] });
    void qc.invalidateQueries({ queryKey: ["submissions"] });
}

// ── The fields (pure controlled fieldset) ───────────────────────────────────

const provLabel = (p: WaterProviderOption) => p.display_name?.trim() || p.name;

/**
 * Jurisdiction picker + portal account + portal link + on-site contact.
 * Pure controlled fields so the customer dialog can embed them in its own
 * form while the site/job dialogs wrap them with a Save of their own —
 * one component, one column, three surfaces.
 */
export function WaterReportingFields({
    value,
    onChange,
    providers,
    contacts,
    legacyAccountNumber,
    savedProviderName,
    showContact = true,
}: {
    value: WaterReportingValue;
    onChange: (next: WaterReportingValue) => void;
    providers: WaterProviderOption[];
    contacts?: SiteContactOption[];
    /** `sites.account_number` — informational display ONLY, never editable here. */
    legacyAccountNumber?: string | null;
    /**
     * Name of the provider the location is saved with. Shown when that
     * provider is turned off and so missing from `providers` — the picker
     * keeps it selected instead of showing "Not sure" and wiping it on save.
     */
    savedProviderName?: string | null;
    showContact?: boolean;
}) {
    const provider = providers.find((p) => p.id === value.waterProviderId) ?? null;
    // Saved with a provider that's turned off (not in the turned-on list).
    // Wait for the list to load so a slow fetch never looks "turned off".
    const turnedOffId = value.waterProviderId && !provider && providers.length > 0 ? value.waterProviderId : null;
    const platform = provider?.submission_platform?.trim() || null;
    const portalUrl = provider?.portal_url?.trim() || null;
    const isOnline = provider?.is_portal === true;

    // Two-step jurisdiction picker: cities served by multiple public water
    // systems are a parent city with PWS children; standalone providers
    // select directly.
    const childrenByParent = useMemo(() => {
        const m = new Map<string, WaterProviderOption[]>();
        for (const p of providers) {
            if (p.parent_water_provider_id) {
                const arr = m.get(p.parent_water_provider_id) ?? [];
                arr.push(p);
                m.set(p.parent_water_provider_id, arr);
            }
        }
        return m;
    }, [providers]);
    const topLevelProviders = useMemo(
        () =>
            providers
                .filter((p) => !p.parent_water_provider_id)
                .slice()
                .sort((a, b) => provLabel(a).localeCompare(provLabel(b), undefined, { sensitivity: "base" })),
        [providers],
    );
    const selectedCityId = provider?.parent_water_provider_id ?? value.waterProviderId;
    const subProviders = selectedCityId ? (childrenByParent.get(selectedCityId) ?? []) : [];

    return (
        <div className="space-y-3">
            <div className="space-y-1">
                <label className="text-sm font-medium">Water provider / jurisdiction</label>
                <Select
                    data-testid="water-jurisdiction-select"
                    value={selectedCityId}
                    onChange={(e) => {
                        const cityId = e.target.value;
                        const kids = childrenByParent.get(cityId) ?? [];
                        // A different jurisdiction means a different reporting
                        // platform — the old portal account can't apply.
                        onChange({
                            ...value,
                            waterProviderId: kids.length > 0 ? "" : cityId,
                            waterAccountNumber: "",
                        });
                    }}
                >
                    <option value="">— Not sure / look up later —</option>
                    {turnedOffId && (
                        <option value={turnedOffId}>
                            {savedProviderName?.trim() || "Saved water provider"} (turned off)
                        </option>
                    )}
                    {topLevelProviders.map((p) => {
                        const hasKids = (childrenByParent.get(p.id) ?? []).length > 0;
                        return (
                            <option key={p.id} value={p.id}>
                                {provLabel(p)}
                                {hasKids
                                    ? " (multiple PWS)"
                                    : p.submission_platform
                                      ? ` (${p.submission_platform})`
                                      : ""}
                            </option>
                        );
                    })}
                </Select>
            </div>
            {turnedOffId && (
                <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="turned-off-provider-note">
                    This water provider is turned off in Compliance. It stays on this location until you pick a
                    different one.
                </p>
            )}
            {subProviders.length > 0 && (
                <div className="space-y-1">
                    <label className="text-sm font-medium">Which PWS serves this location?</label>
                    <Select
                        value={provider?.parent_water_provider_id ? value.waterProviderId : ""}
                        onChange={(e) =>
                            onChange({ ...value, waterProviderId: e.target.value, waterAccountNumber: "" })
                        }
                    >
                        <option value="">— Select the public water system —</option>
                        {subProviders
                            .slice()
                            .sort((a, b) =>
                                provLabel(a).localeCompare(provLabel(b), undefined, { sensitivity: "base" }),
                            )
                            .map((p) => (
                                <option key={p.id} value={p.id}>
                                    {provLabel(p)}
                                    {p.submission_platform ? ` (${p.submission_platform})` : ""}
                                </option>
                            ))}
                    </Select>
                    <p className="text-xs text-foreground/70">
                        This city has multiple public water systems — pick the one that serves this address so reports
                        route correctly.
                    </p>
                </div>
            )}
            {provider && !isOnline && (
                <p className="text-xs text-foreground/60">
                    This jurisdiction reports offline — no portal account number needed.
                </p>
            )}
            {provider && isOnline && (
                <div className="space-y-1">
                    <label className="text-sm font-medium">
                        {platform ? `${platform} account number` : "Reporting platform account number"}
                    </label>
                    <div className="flex gap-2">
                        <Input
                            data-testid="portal-account-input"
                            placeholder="e.g. 12345-A"
                            value={value.waterAccountNumber}
                            onChange={(e) => onChange({ ...value, waterAccountNumber: e.target.value })}
                            className="flex-1 font-mono"
                            maxLength={120}
                        />
                        {portalUrl && (
                            <Button type="button" variant="outline" onClick={() => openExternalLink(portalUrl)}>
                                <ExternalLink className="mr-1 h-4 w-4" />
                                Open {platform ?? "portal"}
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-foreground/80">Saves on this address so every future job reuses it.</p>
                </div>
            )}
            {legacyAccountNumber?.trim() && (
                <p className="text-xs text-foreground/60" data-testid="legacy-st-code">
                    Legacy ST location code: <span className="font-mono">{legacyAccountNumber.trim()}</span> (not the
                    portal account — kept for old-system reference only)
                </p>
            )}
            {showContact && (
                <div className="grid gap-3 rounded-lg border border-border/70 bg-muted/20 p-3 sm:grid-cols-2">
                    <div className="sm:col-span-2 flex items-center gap-1.5 text-sm font-semibold">
                        <Users className="h-4 w-4" /> On-site contact
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">On-site phone</label>
                        <Input
                            placeholder="Reception / property manager"
                            value={value.phone}
                            onChange={(e) => onChange({ ...value, phone: e.target.value })}
                            maxLength={40}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-medium">Primary contact</label>
                        <Select
                            value={value.defaultContactId}
                            onChange={(e) => onChange({ ...value, defaultContactId: e.target.value })}
                        >
                            <option value="">— None —</option>
                            {(contacts ?? []).map((c) => (
                                <option key={c.id} value={c.id}>
                                    {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                                    {c.role_title ? ` · ${c.role_title}` : ""}
                                </option>
                            ))}
                        </Select>
                        <p className="text-xs text-foreground/70">
                            Prints on jurisdiction reports as the site contact.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Self-contained dialog (site page + job page mount this) ─────────────────

interface SiteWaterRead {
    id: string;
    customer_id: string;
    water_provider_id: string | null;
    water_provider_name?: string | null;
    water_provider_display_name?: string | null;
    water_account_number?: string | null;
    account_number: string | null;
    phone?: string | null;
    default_contact_id?: string | null;
}

/** Body every surface sends. NEVER contains `accountNumber` — the legacy
 *  column is read-only from the product's point of view. */
export function buildWaterReportingPatch(v: WaterReportingValue): Record<string, unknown> {
    return {
        waterProviderId: v.waterProviderId || null,
        // An empty provider here is always the office's own pick ("Not sure").
        // The server needs this to clear a turned-off provider; without it a
        // null leaves a turned-off one in place.
        ...(v.waterProviderId ? {} : { clearWaterProvider: true }),
        waterAccountNumber: v.waterAccountNumber.trim() || null,
        phone: v.phone.trim() || null,
        defaultContactId: v.defaultContactId || null,
    };
}

/**
 * The one Water & Reporting edit dialog. Reads the site FRESH by id (its own
 * query key — never a cached list row, which is how the Edit Location dialog
 * once showed a placeholder while the site columns held the real number),
 * then PATCHes the canonical columns and invalidates every surface.
 */
export function WaterReportingEditorDialog({
    siteId,
    customerId,
    onClose,
    onSaved,
}: {
    siteId: string;
    /** Optional — resolved from the site read when not supplied. */
    customerId?: string | null;
    onClose: () => void;
    onSaved?: () => void;
}) {
    const qc = useQueryClient();
    const siteQ = useQuery({
        queryKey: ["site-water", siteId],
        queryFn: () => api.get<{ site: SiteWaterRead }>(`/api/sites/${siteId}`),
        // Always re-read on mount: this dialog must never open on a stale row.
        staleTime: 0,
        refetchOnMount: "always",
    });
    const site = siteQ.data?.site ?? null;
    const resolvedCustomerId = customerId ?? site?.customer_id ?? null;
    const providersQ = useWaterProviders();
    const contactsQ = useSiteContacts(resolvedCustomerId);

    const [draft, setDraft] = useState<WaterReportingValue | null>(null);
    const value: WaterReportingValue = draft ?? {
        waterProviderId: site?.water_provider_id ?? "",
        waterAccountNumber: site?.water_account_number?.trim() ?? "",
        phone: site?.phone ?? "",
        defaultContactId: site?.default_contact_id ?? "",
    };

    const save = useMutation({
        mutationFn: () => api.patch(`/api/sites/${siteId}`, buildWaterReportingPatch(value)),
        onSuccess: () => {
            toast.success("Water & reporting saved — every page reflects it now.");
            invalidateWaterReporting(qc, { siteId, customerId: resolvedCustomerId });
            onSaved?.();
            onClose();
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't save water & reporting"),
    });

    return (
        <Dialog open onClose={onClose} title="Water & Reporting" className="max-w-lg">
            {siteQ.isLoading || !site ? (
                <div className="flex items-center justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-foreground/60" />
                </div>
            ) : (
                <form
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        save.mutate();
                    }}
                >
                    <div className="flex items-center gap-1.5 text-xs text-foreground/70">
                        <Droplet className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />
                        Jurisdiction, portal account, and on-site contact live on the location — every job here inherits
                        them.
                    </div>
                    <WaterReportingFields
                        value={value}
                        onChange={setDraft}
                        providers={providersQ.data?.providers ?? []}
                        contacts={contactsQ.data?.contacts ?? []}
                        legacyAccountNumber={site.account_number}
                        savedProviderName={site.water_provider_display_name || site.water_provider_name}
                    />
                    <div className="flex justify-end gap-2 border-t pt-3">
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={save.isPending}>
                            {save.isPending ? (
                                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            ) : (
                                <Save className="mr-1 h-4 w-4" />
                            )}
                            Save
                        </Button>
                    </div>
                </form>
            )}
        </Dialog>
    );
}
